import { useParams, Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { videos, categories } from "@/mocks/videos";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const article = useMemo(() => {
    return videos.find((v) => v.id === Number(id));
  }, [id]);

  const relatedVideos = useMemo(() => {
    if (!article) return [];
    return videos
      .filter((v) => v.id !== article.id && v.category === article.category)
      .slice(0, 4);
  }, [article]);

  const categoryName = useMemo(() => {
    if (!article) return "";
    return categories.find((c) => c.slug === article.category)?.name || "";
  }, [article]);

  if (!article) {
    return (
      <>
        <SEO title="视频未找到" />
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-foreground-600 text-sm mb-4">该视频可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 cursor-pointer transition-all whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    datePublished: article.date,
    author: {
      "@type": "Organization",
      name: "久久视频",
    },
    publisher: {
      "@type": "Organization",
      name: "久久视频",
    },
    image: article.thumbnail,
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.description}
        keywords={`久久视频,${categoryName},${article.title},免费在线观看`}
        ogType="article"
        ogImage={article.thumbnail}
        schema={articleSchema}
      />
      <Navbar />

      <main className="pt-20">
        <article className="w-full bg-background-50 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <div className="py-6">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-100 text-primary-600 text-xs rounded-full font-medium cursor-pointer hover:bg-primary-200 transition-colors whitespace-nowrap"
                >
                  {categoryName}
                </Link>
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views} 次观看
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="relative aspect-video rounded-xl overflow-hidden mb-8 bg-background-200">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/90 flex items-center justify-center cursor-pointer hover:bg-primary-600/90 transition-all hover:scale-110">
                    <i className="ri-play-fill text-white text-2xl md:text-3xl ml-1"></i>
                  </div>
                </div>
                <div className="absolute bottom-3 right-3 px-3 py-1 bg-black/70 text-white text-sm rounded font-label">
                  {article.duration}
                </div>
              </div>

              <div className="prose prose-sm max-w-none mb-10">
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                  {article.description}
                </p>
                <p className="text-foreground-600 leading-relaxed text-sm md:text-base mt-4">
                  久久视频为您精选优质{categoryName}内容，每日更新最新视频资源。无论是{categoryName === "综艺" ? "热门综艺节目" : categoryName === "动漫" ? "精彩动漫番剧" : categoryName === "短视频" ? "创意短视频" : categoryName === "搞笑创意" ? "爆笑搞笑视频" : `${categoryName}精彩内容`}，久久视频都能满足您的观看需求。打开久久视频，轻松刷不停！
                </p>
              </div>

              <div className="flex items-center gap-4 py-6 border-t border-background-200/70">
                <span className="text-sm text-foreground-600">分享到：</span>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 cursor-pointer transition-all" rel="nofollow">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 cursor-pointer transition-all" rel="nofollow">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 cursor-pointer transition-all" rel="nofollow">
                  <i className="ri-link"></i>
                </a>
              </div>
            </div>
          </div>
        </article>

        {relatedVideos.length > 0 && (
          <section className="w-full bg-background-100/50 py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedVideos.map((video) => (
                  <VideoCard
                    key={video.id}
                    id={video.id}
                    title={video.title}
                    thumbnail={video.thumbnail}
                    duration={video.duration}
                    views={video.views}
                    date={video.date}
                    category={video.category}
                    categoryName={
                      categories.find((c) => c.slug === video.category)?.name ||
                      video.category
                    }
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}